/**
 * Elafiya Shopify Theme - Wishlist System
 * ========================================
 * Client-side wishlist backed by localStorage.
 * Products are identified by their **handle** (slug) so that fetching
 * product data via /products/{handle}.js always works.
 *
 * Features:
 *  - Add / remove / toggle products
 *  - Heart button state sync across the page
 *  - Cross-tab sync via the `storage` event
 *  - Wishlist count badge updates
 *  - Full wishlist page rendering (/pages/wishlist)
 *  - Custom `wishlist-updated` event for other scripts to hook into
 *
 * Usage in Liquid:
 *   <button class="wishlist-btn" data-product-id="{{ product.handle }}" aria-label="Add to wishlist">
 *     <svg>...</svg>
 *   </button>
 *
 *   <span data-wishlist-count>0</span>
 *
 * The wishlist page should include:
 *   <div id="wishlist-grid" class="wishlist-grid"></div>
 *
 * No jQuery or framework dependencies.
 */

(function () {
  'use strict';

  /** Locale/market-aware storefront URL, e.g. "/ar/cart.js" on the Arabic storefront. */
  function shopRoute(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  /* =========================================================================
     WISHLIST CLASS
     ========================================================================= */

  class Wishlist {
    constructor() {
      /** @type {string} localStorage key */
      this.storageKey = 'elafiya_wishlist';

      /** @type {?number} Debounce timer for account sync */
      this._syncTimer = null;

      /** @type {string[]} Array of product handles */
      this.items = this._load();

      this._bindEvents();
      this.updateAllButtons();
      this.updateCount();

      // Hydrate from cart attributes if the customer is logged in
      if (window.__customerLoggedIn) this._loadFromAccount();
    }

    /* ---- Persistence ---------------------------------------------------- */

    /**
     * Load the wishlist array from localStorage.
     *
     * @returns {string[]}
     * @private
     */
    _load() {
      try {
        var data = JSON.parse(localStorage.getItem(this.storageKey));
        return Array.isArray(data) ? data : [];
      } catch (e) {
        return [];
      }
    }

    /**
     * Persist the current wishlist to localStorage and dispatch events.
     *
     * @private
     */
    _save() {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      this.updateCount();

      // Sync to customer account via cart attributes
      if (window.__customerLoggedIn) this._debouncedSync();

      // Custom event for same-tab listeners
      window.dispatchEvent(new CustomEvent('wishlist-updated', {
        detail: { items: this.items }
      }));
    }

    /**
     * Debounced sync to customer account via cart attributes.
     * @private
     */
    _debouncedSync() {
      var self = this;
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(function () {
        self._syncToAccount();
      }, 1000);
    }

    /**
     * Persist wishlist to Shopify cart attributes (survives across sessions for logged-in users).
     * @private
     */
    _syncToAccount() {
      fetch(shopRoute('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _wishlist: JSON.stringify(this.items) } })
      }).catch(function (err) {
        console.warn('[Wishlist] Account sync failed:', err);
      });
    }

    /**
     * On init, load wishlist from cart attributes and merge with localStorage.
     * @private
     */
    async _loadFromAccount() {
      try {
        var res = await fetch(shopRoute('cart.js'));
        var cart = await res.json();
        var raw = cart.attributes && cart.attributes._wishlist;
        if (!raw) return;

        var accountItems = JSON.parse(raw);
        if (!Array.isArray(accountItems)) return;

        // Merge: union of localStorage + account items
        var merged = this.items.slice();
        var changed = false;
        for (var i = 0; i < accountItems.length; i++) {
          if (merged.indexOf(accountItems[i]) === -1) {
            merged.push(accountItems[i]);
            changed = true;
          }
        }

        if (changed) {
          this.items = merged;
          localStorage.setItem(this.storageKey, JSON.stringify(this.items));
          this.updateAllButtons();
          this.updateCount();
        }
      } catch (e) {
        console.warn('[Wishlist] Could not load from account:', e);
      }
    }

    /* ---- Public API ----------------------------------------------------- */

    /**
     * Add a product to the wishlist (by handle).
     *
     * @param {string} productId - Product handle.
     */
    add(productId) {
      if (!productId) return;
      if (!this.items.includes(productId)) {
        this.items.push(productId);
        this._save();
      }
    }

    /**
     * Remove a product from the wishlist.
     *
     * @param {string} productId - Product handle.
     */
    remove(productId) {
      var idx = this.items.indexOf(productId);
      if (idx !== -1) {
        this.items.splice(idx, 1);
        this._save();
      }
    }

    /**
     * Toggle a product in/out of the wishlist.
     *
     * @param {string}  productId - Product handle.
     * @returns {boolean} `true` if the product is now wishlisted.
     */
    toggle(productId) {
      if (this.has(productId)) {
        this.remove(productId);
      } else {
        this.add(productId);
      }
      return this.has(productId);
    }

    /**
     * Check whether a product is in the wishlist.
     *
     * @param {string} productId - Product handle.
     * @returns {boolean}
     */
    has(productId) {
      return this.items.includes(productId);
    }

    /**
     * Return a copy of all wishlisted product handles.
     *
     * @returns {string[]}
     */
    getAll() {
      return this.items.slice();
    }

    /**
     * Return the number of wishlisted products.
     *
     * @returns {number}
     */
    getCount() {
      return this.items.length;
    }

    /**
     * Clear the entire wishlist.
     */
    clear() {
      this.items = [];
      this._save();
      this.updateAllButtons();
    }

    /* ---- UI Updates ----------------------------------------------------- */

    /**
     * Update every [data-wishlist-count] badge on the page.
     */
    updateCount() {
      var count = this.getCount();
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    }

    /**
     * Synchronise the `is-wishlisted` class on every `.wishlist-btn`
     * currently in the DOM.
     */
    updateAllButtons() {
      var self = this;
      document.querySelectorAll('.wishlist-btn').forEach(function (btn) {
        var productId = btn.dataset.productId;
        if (!productId) return;
        var wishlisted = self.has(productId);
        btn.classList.toggle('is-wishlisted', wishlisted);
        btn.setAttribute('aria-pressed', wishlisted);
        btn.setAttribute('aria-label', wishlisted ? 'Remove from wishlist' : 'Add to wishlist');
      });
    }

    /* ---- Event Binding -------------------------------------------------- */

    /**
     * Set up all event listeners.
     *
     * @private
     */
    _bindEvents() {
      var self = this;

      // Delegated click on any .wishlist-btn
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('.wishlist-btn');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        // Redirect to login if user is not logged in
        if (!window.__customerLoggedIn) {
          window.location.href = (window.__loginUrl || '/account/login');
          return;
        }

        var productId = btn.dataset.productId;
        if (!productId) return;

        var isWishlisted = self.toggle(productId);

        // Update this specific button immediately
        btn.classList.toggle('is-wishlisted', isWishlisted);
        btn.setAttribute('aria-pressed', isWishlisted);
        btn.setAttribute('aria-label', isWishlisted ? 'Remove from wishlist' : 'Add to wishlist');

        // Also update any duplicate buttons for the same product (e.g. in a grid + quick view)
        document.querySelectorAll('.wishlist-btn[data-product-id="' + productId + '"]').forEach(function (otherBtn) {
          if (otherBtn === btn) return;
          otherBtn.classList.toggle('is-wishlisted', isWishlisted);
          otherBtn.setAttribute('aria-pressed', isWishlisted);
        });

        self.updateCount();

        // Optional animation class (removed after transition ends)
        btn.classList.add('wishlist-btn--animating');
        btn.addEventListener('animationend', function handler() {
          btn.classList.remove('wishlist-btn--animating');
          btn.removeEventListener('animationend', handler);
        });
      });

      // Cross-tab sync: another tab changed localStorage
      window.addEventListener('storage', function (e) {
        if (e.key === self.storageKey) {
          self.items = self._load();
          self.updateAllButtons();
          self.updateCount();
        }
      });

      // Re-sync buttons after Shopify section rendering (e.g. AJAX pagination)
      document.addEventListener('shopify:section:load', function () {
        self.updateAllButtons();
        self.updateCount();
      });
    }

    /* ---- Wishlist Page Rendering ---------------------------------------- */

    /**
     * Render the full wishlist grid on the dedicated /pages/wishlist page.
     * Fetches product data from the Shopify AJAX API by handle.
     */
    async renderWishlistPage() {
      var container = document.getElementById('wishlist-grid');
      if (!container) return;

      var loadingEl = document.getElementById('wishlist-loading');
      var emptyEl = document.getElementById('wishlist-empty');
      var countEl = document.getElementById('wishlist-page-count');

      // Show loading state
      if (loadingEl) loadingEl.style.display = '';
      if (emptyEl) emptyEl.style.display = 'none';
      container.innerHTML = '';

      // Empty wishlist
      if (this.items.length === 0) {
        if (loadingEl) loadingEl.style.display = 'none';
        this._renderEmpty(container, emptyEl);
        if (countEl) countEl.textContent = '0';
        return;
      }

      // Fetch product data for every handle in parallel
      var productPromises = this.items.map(function (handle) {
        return fetch(shopRoute('products/' + encodeURIComponent(handle) + '.js'))
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      });

      var products = await Promise.all(productPromises);

      // Filter out any products that could not be fetched (deleted, draft, etc.)
      products = products.filter(Boolean);

      if (loadingEl) loadingEl.style.display = 'none';

      // Clean up stale handles that no longer resolve
      if (products.length !== this.items.length) {
        var validHandles = products.map(function (p) { return p.handle; });
        this.items = this.items.filter(function (h) { return validHandles.includes(h); });
        this._save();
      }

      // If no valid products remain, show empty state
      if (products.length === 0) {
        this._renderEmpty(container, emptyEl);
        if (countEl) countEl.textContent = '0';
        return;
      }

      if (countEl) countEl.textContent = products.length;

      // Render the product cards
      container.innerHTML = products.map(function (product) {
        var image = product.featured_image
          || '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

        var price = Shopify.formatMoney(product.price);

        var compareAtPrice = '';
        if (product.compare_at_price && product.compare_at_price > product.price) {
          compareAtPrice = '<span class="product-card__compare-price">' + Shopify.formatMoney(product.compare_at_price) + '</span>';
        }

        var badge = '';
        if (product.compare_at_price && product.compare_at_price > product.price) {
          var discount = Math.round((1 - product.price / product.compare_at_price) * 100);
          badge = '<span class="product-card__badge product-card__badge--sale">-' + discount + '%</span>';
        }
        if (!product.available) {
          badge = '<span class="product-card__badge product-card__badge--soldout">Sold Out</span>';
        }

        return '\
          <div class="product-card" data-product-handle="' + product.handle + '">\
            <div class="product-card__image-wrapper">\
              ' + badge + '\
              <button class="wishlist-btn is-wishlisted" data-product-id="' + product.handle + '" aria-label="Remove from wishlist" aria-pressed="true">\
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">\
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>\
                </svg>\
              </button>\
              <a href="' + product.url + '" class="product-card__image-link">\
                <img src="' + image + '" alt="' + _escapeHtml(product.title) + '" loading="lazy" width="400" height="400">\
              </a>\
            </div>\
            <div class="product-card__info">\
              <div class="product-card__vendor">' + _escapeHtml(product.vendor) + '</div>\
              <h3 class="product-card__title"><a href="' + product.url + '">' + _escapeHtml(product.title) + '</a></h3>\
              <div class="product-card__price">\
                <span class="product-card__current-price">' + price + '</span>\
                ' + compareAtPrice + '\
              </div>\
              ' + _renderQuickAdd(product) + '\
            </div>\
          </div>';
      }).join('');

      // Re-bind buttons (they were just created)
      this.updateAllButtons();
    }

    /**
     * Show the empty wishlist state.
     *
     * @param {HTMLElement}      container
     * @param {HTMLElement|null} emptyEl - A pre-existing empty-state element.
     * @private
     */
    _renderEmpty(container, emptyEl) {
      if (emptyEl) {
        emptyEl.style.display = '';
      } else {
        container.innerHTML = '\
          <div class="wishlist-empty">\
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">\
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>\
            </svg>\
            <h2>Your wishlist is empty</h2>\
            <p>Browse our collections and heart the products you love.</p>\
            <a href="' + ((window.__localeRoot || '').replace(/\/+$/, '') || '') + '/collections/all" class="btn btn--primary">Start Shopping</a>\
          </div>';
      }
    }
  }


  /* =========================================================================
     HELPER FUNCTIONS (module-private)
     ========================================================================= */

  /**
   * Escape HTML special characters to prevent XSS when injecting
   * product data into innerHTML.
   *
   * @param {string} str
   * @returns {string}
   */
  function _escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Render a "Quick Add" button for the wishlist page.
   * If the product has only one variant (Default Title), show a direct
   * add-to-cart button. Otherwise link to the product page.
   *
   * @param {Object} product - Shopify product object from /products/{handle}.js
   * @returns {string} HTML string.
   */
  function _renderQuickAdd(product) {
    if (!product.available) {
      return '<button class="btn btn--secondary btn--small" disabled>Sold Out</button>';
    }

    // Single-variant product: add directly
    if (product.variants && product.variants.length === 1) {
      return '<button class="btn btn--primary btn--small wishlist-quick-add" data-variant-id="' + product.variants[0].id + '" aria-label="Add ' + _escapeHtml(product.title) + ' to cart">Add to Cart</button>';
    }

    // Multi-variant: link to product page
    return '<a href="' + product.url + '" class="btn btn--secondary btn--small">Select Options</a>';
  }


  /* =========================================================================
     QUICK ADD FROM WISHLIST PAGE
     Intercept clicks on .wishlist-quick-add buttons and use the global
     cartDrawer instance (from theme.js) to add items via AJAX.
     ========================================================================= */

  function _bindQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.wishlist-quick-add');
      if (!btn) return;

      e.preventDefault();

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Disable button and show feedback (localized via quick-add strings)
      var _wt = window.__qaTranslations || {};
      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = _wt.adding || 'Adding...';

      function _wlSuccess() {
        btn.textContent = _wt.added || 'Added!';
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      }
      function _wlFail() {
        btn.textContent = _wt.tryAgain || 'Try Again';
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }

      // Use the global cartDrawer if available (from theme.js).
      // addItem THROWS on failure — never show "Added!" unless it resolves.
      if (window.cartDrawer && typeof window.cartDrawer.addItem === 'function') {
        window.cartDrawer.addItem(variantId, 1).then(_wlSuccess).catch(_wlFail);
      } else {
        // Fallback: direct AJAX call
        fetch(shopRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add to cart failed: ' + res.status);
            return res.json();
          })
          .then(_wlSuccess)
          .catch(_wlFail);
      }
    });
  }


  /* =========================================================================
     INITIALISATION
     ========================================================================= */

  document.addEventListener('DOMContentLoaded', function () {
    // Create the global wishlist instance
    window.wishlist = new Wishlist();

    // Bind quick-add buttons (used on the wishlist page)
    _bindQuickAdd();

    // If we are on the wishlist page, render the product grid
    if (document.getElementById('wishlist-grid')) {
      window.wishlist.renderWishlistPage();
    }

    console.log('[Elafiya] Wishlist JS initialised.');
  });

})();
